/**
 * Geoapify Static Maps API - uses POST to bypass URL length limits.
 * Shows two markers (port A, destination B) and route polyline.
 */

import {
  canSpendCredits,
  recordCreditsUsed,
  estimateStaticMapCredits,
} from "./geoapifyCreditTracker";

const STATIC_MAP_BASE = "https://maps.geoapify.com/v1/staticmap";
const apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY;

/** Sample every Nth waypoint for polyline (keeps ~130 points from 1288) */
const SAMPLE_EVERY_NTH = 10;

export interface Coords {
  lat: number;
  lon: number;
}

function sampleEveryNth<T>(points: T[], everyNth: number): T[] {
  if (points.length <= everyNth) return points;
  const result: T[] = [points[0]];
  for (let i = everyNth; i < points.length - 1; i += everyNth) {
    result.push(points[i]);
  }
  if (points.length > 1) {
    result.push(points[points.length - 1]);
  }
  return result;
}

/**
 * Fetch static map image via POST (no URL length limit).
 * Returns blob URL or null on failure.
 */
export async function fetchRouteMapBlob(
  portCoords: Coords,
  destCoords: Coords,
  routeGeometry?: number[][],
  width = 400,
  height = 300,
): Promise<string | null> {
  if (!apiKey || apiKey === "your_geoapify_api_key_here") {
    return null;
  }
  const mapCredits = estimateStaticMapCredits(width, height, 2);
  if (!canSpendCredits(mapCredits)) {
    return null;
  }

  const markers = [
    {
      lon: portCoords.lon,
      lat: portCoords.lat,
      color: "#1f63e6",
      size: 32,
      type: "circle",
      text: "A",
      contentsize: 14,
      contentcolor: "#ffffff",
      whitecircle: "no",
    },
    {
      lon: destCoords.lon,
      lat: destCoords.lat,
      color: "#e63e1f",
      size: 32,
      type: "circle",
      text: "B",
      contentsize: 14,
      contentcolor: "#ffffff",
      whitecircle: "no",
    },
  ];

  const body: Record<string, unknown> = {
    style: "klokantech-basic",
    width,
    height,
    scaleFactor: 2,
    format: "png",
    markers,
  };

  if (routeGeometry && routeGeometry.length >= 2) {
    const sampled = sampleEveryNth(routeGeometry, SAMPLE_EVERY_NTH);
    const value = sampled.map(([lon, lat]) => ({ lat, lon }));
    body.geometries = [
      {
        type: "polyline",
        linecolor: "#1f63e6",
        linewidth: 4,
        value,
      },
    ];
  }

  try {
    const res = await fetch(`${STATIC_MAP_BASE}?apiKey=${apiKey}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      return null;
    }

    recordCreditsUsed(mapCredits);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * @deprecated Use fetchRouteMapBlob instead - GET URLs exceed length limit
 */
export function buildRouteMapUrl(
  portCoords: Coords,
  destCoords: Coords,
  routeGeometry?: number[][],
): string | null {
  return null;
}
